import { http } from '@/lib/axios';
import { unwrap, toApiError } from '@/lib/unwrap';
import type { ApiEnvelope } from '@/types/api';
import type { DeviceOption, UserDeviceBinding } from '@/types/device';

/** GET /user-device — current binding for the logged-in cashier (or null). */
export async function getUserDevice(): Promise<UserDeviceBinding | null> {
  try {
    const res = await http.get<ApiEnvelope<UserDeviceBinding | null>>('/user-device');
    return unwrap(res.data);
  } catch (error) {
    const err = toApiError(error);
    if (err.code === 404) return null;
    throw err;
  }
}

export interface BindDevicePayload {
  name: string;
  type: 'TICKETBOX';
  ticketPrinterId: string | null;
  cashlessReaderId: string | null;
  /**
   * The Permata card terminal bound to this counter. Distinct from
   * `cashlessReaderId` on purpose: a counter can hold BOTH a contactless
   * e-money reader and a card EDC, and the sale path resolves the card
   * terminal from this slot alone.
   */
  edcTerminalId: string | null;
}

export async function bindUserDevice(
  payload: BindDevicePayload,
): Promise<UserDeviceBinding> {
  try {
    const res = await http.put<ApiEnvelope<UserDeviceBinding>>('/user-device', payload);
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/** GET /device?... — available devices to bind, filtered by deviceType. */
export async function getDevices(deviceType?: string): Promise<DeviceOption[]> {
  try {
    const params = deviceType ? { deviceType } : undefined;
    const res = await http.get<ApiEnvelope<DeviceOption[]>>('/device', { params });
    const data = unwrap(res.data);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    const err = toApiError(error);
    // Device list may be admin-gated for some cashier deployments — fail open.
    if (err.code === 403 || err.code === 404) return [];
    throw err;
  }
}
